import {
  AhpErrorCodes,
  type ContentEncoding,
  type ResourceReadResult,
  type SessionActiveClient,
} from '@microsoft/agent-host-protocol'
import {RpcError, type ResourceRequestHandlers} from '@microsoft/agent-host-protocol/client'

const PLUGIN_DIRECTORY = 'copilot-custom-instructions'
const PLUGIN_MANIFEST = '{"name":"copilot-custom-instructions","version":"1.0.0"}'
const CONTENT_ENCODING_UTF8 = 'utf-8' as ContentEncoding

export interface RuleFile {
  name: string
  content: string
}

type ClientPluginCustomization = NonNullable<SessionActiveClient['customizations']>[number]

function contentHash(content: string): string {
  let first = 0x811c9dc5
  let second = 0x9e3779b9

  for (const byte of new TextEncoder().encode(content)) {
    first = Math.imul(first ^ byte, 0x01000193)
    second = Math.imul(second ^ byte, 0x85ebca6b)
  }

  return [first, second].map(value => (value >>> 0).toString(16).padStart(8, '0')).join('')
}

function notFound(uri: string): never {
  throw new RpcError(AhpErrorCodes.NotFound, `resource not found: ${uri}`)
}

function validateRuleName(name: string): void {
  if (!name || name === '.' || name === '..' || name.includes('/') || name.includes('\\') || name.includes('\0')) {
    throw new TypeError(`invalid instruction rule filename: ${JSON.stringify(name)}`)
  }
}

export class InstructionPluginSource {
  private rules = new Map<string, string>()
  private nonce = ''
  private clientId: string | undefined

  setRules(rules: RuleFile[]): boolean {
    for (const rule of rules) validateRuleName(rule.name)
    const normalized = [...new Map(rules.map(rule => [rule.name, rule.content])).entries()]
      .map(([name, content]) => ({name, content}))
      .toSorted((left, right) => left.name.localeCompare(right.name))
    const serialized = JSON.stringify(normalized)
    const nonce = contentHash(serialized)
    if (nonce === this.nonce) return false

    this.rules = new Map(normalized.map(rule => [rule.name, rule.content]))
    this.nonce = nonce
    return true
  }

  hasRules(): boolean {
    return this.rules.size > 0
  }

  getCustomization(clientId: string): ClientPluginCustomization | undefined {
    this.clientId = clientId
    if (!this.hasRules()) return undefined

    return {
      type: 'plugin' as ClientPluginCustomization['type'],
      id: `${PLUGIN_DIRECTORY}:${clientId}`,
      uri: this.rootUri(clientId),
      name: 'Custom instructions',
      enabled: true,
      nonce: this.nonce,
    }
  }

  resourceList(uri: string): {entries: Array<{name: string; type: 'file' | 'directory'}>} {
    const path = this.resolvePath(uri)
    if (path.length === 0) {
      return {
        entries: [
          {name: 'plugin.json', type: 'file'},
          {name: 'rules', type: 'directory'},
        ],
      }
    }
    if (path.length === 1 && path[0] === 'rules') {
      return {entries: [...this.rules.keys()].map(name => ({name, type: 'file'}))}
    }
    return notFound(uri)
  }

  resourceRead(uri: string): ResourceReadResult {
    const path = this.resolvePath(uri)
    if (path.length === 1 && path[0] === 'plugin.json') {
      return {data: PLUGIN_MANIFEST, encoding: CONTENT_ENCODING_UTF8, contentType: 'application/json'}
    }
    if (path.length === 2 && path[0] === 'rules') {
      const name = path[1]
      const content = name ? this.rules.get(name) : undefined
      if (content !== undefined) {
        return {data: content, encoding: CONTENT_ENCODING_UTF8, contentType: 'text/markdown'}
      }
    }
    return notFound(uri)
  }

  createResourceRequestHandlers(logRequest: (method: 'resourceList' | 'resourceRead', uri: string) => void) {
    return {
      resourceList: params => {
        logRequest('resourceList', params.uri)
        return this.resourceList(params.uri)
      },
      resourceRead: params => {
        logRequest('resourceRead', params.uri)
        return this.resourceRead(params.uri)
      },
    } satisfies ResourceRequestHandlers
  }

  private rootUri(clientId: string): string {
    return `virtual://${clientId}/${PLUGIN_DIRECTORY}`
  }

  private resolvePath(uri: string): string[] {
    const clientId = this.clientId
    if (!clientId) return notFound(uri)

    const root = this.rootUri(clientId)
    if (uri !== root && !uri.startsWith(`${root}/`)) return notFound(uri)

    const rawPath = uri.slice(root.length).replace(/^\/|\/$/g, '')
    if (!rawPath) return []

    let segments: string[]
    try {
      segments = rawPath.split('/').map(segment => decodeURIComponent(segment))
    } catch {
      return notFound(uri)
    }
    if (segments.some(segment => !segment || segment === '.' || segment === '..' || segment.includes('/'))) {
      return notFound(uri)
    }
    return segments
  }
}
